import {
  intlFormat,
  intlFormatDistance,
  parse,
  format as dateFnsFormat,
} from 'date-fns'
import { useCurrentLocaleCode } from '@/composables/locale'

export const useBrowserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone

export const format = (date, format) =>
  intlFormat(new Date(date), format, { locale: useCurrentLocaleCode() })

export const formatDate = (date, timeZone) =>
  format(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timeZone ?? useBrowserTimezone(),
  })

export const formatTime = (date, timeZone, showSeconds = true) =>
  format(date, {
    hour: '2-digit',
    minute: '2-digit',
    ...(showSeconds && { second: '2-digit' }),
    timeZone: timeZone ?? useBrowserTimezone(),
  })

export const formatDatetime = (date, timeZone) =>
  format(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timeZone ?? useBrowserTimezone(),
  })

export const parseDate = (date, formatString) => {
  return parse(date, formatString, new Date())
}

export const customFormat = (date, format) => dateFnsFormat(date, format)

/**
 * Coerce a calendar date or datetime input into a Date safe for use as a
 * timezone-offset reference instant. Date-only strings ("YYYY-MM-DD") are
 * pinned to noon UTC so they land on the same civil day in every IANA zone
 * and away from DST transitions; other inputs are parsed as-is.
 */
export const toTimezoneReferenceDate = (value) => {
  if (!value) {
    return new Date()
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T12:00:00Z`)
  }
  return new Date(value)
}

/**
 * Return a display string for a timezone in the form "(Europe/Berlin, GMT+2)"
 * intended to be interpolated into the `{timezone}` placeholder of
 * `bookable.timezone_hint`. The reference date controls DST — pass the
 * relevant sales/availability date when known so the offset reflects the
 * instant the user is editing instead of "now".
 *
 * Falls back to "(Europe/Berlin)" without an offset if Intl formatting
 * fails, or to an empty string if no timezone is provided.
 */
export const getTimezoneHint = (timezone, referenceDate = new Date()) => {
  if (!timezone) {
    return ''
  }
  const locale = useCurrentLocaleCode()
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(referenceDate)
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
    return offset ? `(${timezone}, ${offset})` : `(${timezone})`
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      `getTimezoneHint failed for timezone "${timezone}" and locale "${locale}":`,
      error,
    )
    return `(${timezone})`
  }
}

/**
 * Localised relative distance such as "2 minutes ago" or "in 3 hours",
 * measured against `referenceDate` (defaults to now). Pass a server-provided
 * reference to avoid drift from a skewed browser clock.
 */
export const formatRelative = (date, referenceDate = new Date()) =>
  intlFormatDistance(new Date(date), new Date(referenceDate), {
    locale: useCurrentLocaleCode(),
  })

const zonedParts = (date, timeZone) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(new Date(date))
      .map(({ type, value }) => [type, value]),
  )

/**
 * A moment as the value of a `datetime-local` input in the given timezone
 * ("YYYY-MM-DDTHH:mm").
 */
export const toZonedDatetimeInput = (date, timeZone) => {
  const parts = zonedParts(date, timeZone)

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

const offsetAt = (date, timeZone) => {
  const shown = zonedParts(date, timeZone)

  return (
    Date.UTC(
      Number(shown.year),
      Number(shown.month) - 1,
      Number(shown.day),
      Number(shown.hour),
      Number(shown.minute),
    ) - date.getTime()
  )
}

/**
 * The moment a `datetime-local` value means in the given timezone, as an ISO
 * string in UTC. The offset is looked up twice, the second time at the
 * moment found first, so a value next to a DST switch gets the right one.
 */
export const zonedDatetimeInputToIso = (value, timeZone) => {
  const asUtc = new Date(`${value}:00Z`)
  const guess = new Date(asUtc.getTime() - offsetAt(asUtc, timeZone))

  return new Date(asUtc.getTime() - offsetAt(guess, timeZone)).toISOString()
}
